import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Tag,
  Typography,
  Space,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tooltip,
} from 'antd';
import { EyeOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { setMessage } from '../../../redux/message.reducer';
import './TaskCompletionsList.css';

const { Title } = Typography;
const DEFAULT_PAGE_SIZE = 10;

function openAttachmentUrl(url) {
  if (!url || typeof url !== 'string') return;
  if (url.startsWith('data:')) {
    try {
      fetch(url)
        .then((res) => res.blob())
        .then((blob) => {
          const blobUrl = URL.createObjectURL(blob);
          window.open(blobUrl, '_blank', 'noopener,noreferrer');
          setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        })
        .catch(() => window.open(url, '_blank', 'noopener,noreferrer'));
    } catch {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
const REWARD_POINT_OPTIONS = [0, 10, 20, 25, 50].map((v) => ({ value: v, label: v === 0 ? 'None' : String(v) }));

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'verified', label: 'Verified' },
  { value: 'rejected', label: 'Rejected' },
];

export default function TaskCompletionsList() {
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: DEFAULT_PAGE_SIZE, total: 0 });
  const [statusFilter, setStatusFilter] = useState('pending');
  const [viewRecord, setViewRecord] = useState(null);
  const [verifyModal, setVerifyModal] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [form] = Form.useForm();
  const [actionLoading, setActionLoading] = useState(false);

  const fetchCompletions = useCallback(
    async (page, limit) => {
      setLoading(true);
      try {
        const params = { page, limit };
        if (statusFilter) params.status = statusFilter;
        const res = await axios.get('/task-completions', { params });
        setData(res.data?.data ?? []);
        setPagination((prev) => ({
          ...prev,
          current: res.data?.page ?? page,
          pageSize: res.data?.limit ?? limit,
          total: res.data?.total ?? 0,
        }));
      } catch (err) {
        dispatch(setMessage({ message: err.response?.data?.error || 'Failed to load', type: 'error' }));
        setData([]);
      } finally {
        setLoading(false);
    }
  },
  [dispatch, statusFilter]
  );

  useEffect(() => {
    fetchCompletions(pagination.current, pagination.pageSize);
  }, [pagination.current, pagination.pageSize, statusFilter, fetchCompletions]);

  const handleVerify = async () => {
    if (!verifyModal) return;
    try {
      const values = await form.validateFields();
      setActionLoading(true);
      await axios.put(`/task-completions/${verifyModal._id}/verify`, {
        rewardPoints: values.rewardPoints,
        adminNotes: values.adminNotes?.trim() || '',
      });
      dispatch(setMessage({ message: 'Verified. Reward points granted.', type: 'success' }));
      setVerifyModal(null);
      form.resetFields();
      fetchCompletions(pagination.current, pagination.pageSize);
    } catch (err) {
      if (err.errorFields) return;
      dispatch(setMessage({ message: err.response?.data?.error || 'Verify failed', type: 'error' }));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    try {
      const values = form.getFieldsValue();
      setActionLoading(true);
      await axios.put(`/task-completions/${rejectModal._id}/reject`, {
        adminNotes: values.adminNotes?.trim() || '',
      });
      dispatch(setMessage({ message: 'Rejected.', type: 'success' }));
      setRejectModal(null);
      form.resetFields();
      fetchCompletions(pagination.current, pagination.pageSize);
    } catch (err) {
      dispatch(setMessage({ message: err.response?.data?.error || 'Reject failed', type: 'error' }));
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    {
      title: 'Task',
      key: 'task',
      width: 400,
      render: (_, record) => record.taskId?.title ?? '—',
    },
    {
      title: 'User',
      key: 'user',
      width: 180,
      render: (_, record) => record.userId?.name || record.userId?.email || '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 180,
      render: (status) => (
        <Tag color={status === 'verified' ? 'green' : status === 'rejected' ? 'red' : 'orange'}>
          {STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status}
        </Tag>
      ),
    },
    {
      title: 'Reward pts',
      dataIndex: 'rewardPoints',
      key: 'rewardPoints',
      width: 180,
      render: (val) => (val != null ? val : '—'),
    },
    {
      title: 'Submitted',
      dataIndex: 'submittedAt',
      key: 'submittedAt',
      width: 180,
      render: (val) => (val ? dayjs(val).format('MMM D, YYYY h:mm A') : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 110,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View">
            <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => setViewRecord(record)} />
          </Tooltip>
          {record.status === 'pending' && (
            <>
              <Tooltip title="Verify">
                <Button
                  type="text"
                  size="small"
                  icon={<CheckOutlined />}
                  onClick={() => {
                    setVerifyModal(record);
                    form.setFieldsValue({ rewardPoints: record.taskId?.rewardPoint ?? 0, adminNotes: '' });
                  }}
                />
              </Tooltip>
              <Tooltip title="Reject">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<CloseOutlined />}
                  onClick={() => {
                    setRejectModal(record);
                    form.setFieldsValue({ adminNotes: '' });
                  }}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="task-completions-list-page">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Title level={3} style={{ margin: 0 }}>
          Task completions
        </Title>
        <Card className="task-completions-card">
          <Space style={{ marginBottom: 16 }}>
            <Select
              value={statusFilter || undefined}
              onChange={(v) => {
                setStatusFilter(v ?? '');
                setPagination((prev) => ({ ...prev, current: 1 }));
              }}
              options={[{ value: '', label: 'All' }, ...STATUS_OPTIONS]}
              style={{ width: 120 }}
            />
          </Space>
          <Table
            columns={columns}
            dataSource={data.map((r) => ({ ...r, key: r._id }))}
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (t) => `Total ${t}`,
              pageSizeOptions: ['10', '20', '50'],
            }}
            onChange={(pag) =>
              setPagination((prev) => ({
                ...prev,
                current: pag.current ?? prev.current,
                pageSize: pag.pageSize ?? prev.pageSize,
              }))
            }
            size="middle"
            responsive
            scroll={{ x: 1000 }}
          />
        </Card>
      </Space>

      <Modal
        title={viewRecord?.taskId?.title ? `Completion: ${viewRecord.taskId.title}` : 'Completion details'}
        open={!!viewRecord}
        onCancel={() => setViewRecord(null)}
        footer={[<Button key="close" onClick={() => setViewRecord(null)}>Close</Button>]}
        width={560}
        destroyOnClose
      >
        {viewRecord && (
          <div className="task-completions-view">
            <div className="task-completions-view-row">
              <span className="task-completions-view-label">User</span>
              <span>{viewRecord.userId?.name || viewRecord.userId?.email || '—'}</span>
            </div>
            <div className="task-completions-view-row">
              <span className="task-completions-view-label">Status</span>
              <Tag color={viewRecord.status === 'verified' ? 'green' : viewRecord.status === 'rejected' ? 'red' : 'orange'}>
                {STATUS_OPTIONS.find((o) => o.value === viewRecord.status)?.label ?? viewRecord.status}
              </Tag>
            </div>
            {viewRecord.rewardPoints != null && (
              <div className="task-completions-view-row">
                <span className="task-completions-view-label">Reward points</span>
                <span>{viewRecord.rewardPoints}</span>
              </div>
            )}
            <div className="task-completions-view-row">
              <span className="task-completions-view-label">Submitted</span>
              <span>{viewRecord.submittedAt ? dayjs(viewRecord.submittedAt).format('MMM D, YYYY h:mm A') : '—'}</span>
            </div>
            {viewRecord.description && (
              <div className="task-completions-view-section">
                <div className="task-completions-view-section-title">Description</div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{viewRecord.description}</div>
              </div>
            )}
            {Array.isArray(viewRecord.workResult) && viewRecord.workResult.length > 0 && (
              <div className="task-completions-view-section">
                <div className="task-completions-view-section-title">Work result</div>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {viewRecord.workResult.map((f, i) => (
                    <li key={i}>
                      <a
                        href="#"
                        onClick={(e) => { e.preventDefault(); openAttachmentUrl(f.url); }}
                      >
                        {f.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title="Verify and grant reward points"
        open={!!verifyModal}
        onCancel={() => { setVerifyModal(null); form.resetFields(); }}
        onOk={handleVerify}
        confirmLoading={actionLoading}
        okText="Verify"
        destroyOnClose
      >
        {verifyModal && (
          <Form form={form} layout="vertical">
            <Form.Item
              name="rewardPoints"
              label="Reward points"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Select options={REWARD_POINT_OPTIONS} placeholder="Select points" />
            </Form.Item>
            <Form.Item name="adminNotes" label="Notes (optional)">
              <Input.TextArea rows={2} placeholder="Optional note" />
            </Form.Item>
          </Form>
        )}
      </Modal>

      <Modal
        title="Reject completion"
        open={!!rejectModal}
        onCancel={() => { setRejectModal(null); form.resetFields(); }}
        onOk={handleReject}
        confirmLoading={actionLoading}
        okText="Reject"
        destroyOnClose
      >
        {rejectModal && (
          <Form form={form} layout="vertical">
            <Form.Item name="adminNotes" label="Reason (optional)">
              <Input.TextArea rows={2} placeholder="Optional reason for rejection" />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
}
